/**
 * This file contains helper functions to work with known validation state, ie functions
 * to process ProcedureValidationError objects.
 */

import isEqual from 'react-fast-compare';
import _ from 'underscore';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BioentityLinkSchemaFieldTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  getModelRequiredLinkFromTagSchema,
  SchemaInterfaceWithFields,
} from 'procedures/design/utils/equipmentSchemaHelpers';
import {
  getMethodComponentCid,
  getMethodComponentName,
  getMethodComponentUnitString,
  isEquipmentSettingComponent,
  MethodComponent,
  MethodNode,
  ProcedureEquipmentSetting,
  ProcedureMaterialInputSetting,
} from 'procedures/ProcedureDomainTypes';
import {extractMethodNodeFromProcessNode} from 'procedures/processes/MethodNodeUtils';
import {TagSchemaForProcedures} from 'procedures/utils/useAllTagSchemas';
import {BioentityForProcedure} from 'procedures/utils/useBioentitiesForProcedures';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  isRequiredLinkCustomSchemaInterface,
  isRequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {assertNever} from 'util/Types';

import {
  ProcedureValidationError,
  ProcedureValidationErrorsFilterCriteria,
  ProcedureValidationErrorType,
  ProcedureValidationObject,
  ProcedureValidationObjectType,
  ProcedureValidationErrorsForPanel,
} from './ProcedureValidationTypes';

/**
 * Returns an array of ProcedureValidationError that match ALL filter criteria specified.
 * Errors that only partially match the filter criteria will not be considered a match.
 * Null filter criteria fields will not be evaluated.
 */
export function filterProcedureValidationErrors(
  validationErrors: Array<ProcedureValidationError>,
  filterCriteria: ProcedureValidationErrorsFilterCriteria = {}
): Array<ProcedureValidationError> {
  const filterKeys = Object.keys(filterCriteria);

  return validationErrors.filter((error) =>
    // ESLint doesn't know that a return in the default case would be unreachable
    // oxlint-disable-next-line array-callback-return
    filterKeys.every((key: keyof ProcedureValidationErrorsFilterCriteria) => {
      if (filterCriteria[key] == null) {
        return true; // this filter not provided
      }
      switch (key) {
        case 'errorTypes':
          return filterCriteria.errorTypes!.includes(error.type);
        case 'primaryObjectType':
          return filterCriteria.primaryObjectType! === error.primaryObject.objectType;
        case 'primaryObjectId':
          return filterCriteria.primaryObjectId! === error.primaryObject.id;
        case 'parentObjectType':
          return error.parentObjects.find((o) => o.objectType === filterCriteria.parentObjectType!);
        case 'parentObjectId':
          return error.parentObjects.find((o) => o.id === filterCriteria.parentObjectId!);
        default:
          assert(false, assertNever(key));
      }
    })
  );
}

/**
 * Finds errors that have a matching primaryObject, or has a parentObject matching the primaryObject
 * Note, does not include deeper dependencies like "child of child".
 */
export function findRelevantProcedureValidationErrors(
  validationErrors: Array<ProcedureValidationError>,
  primaryObject: ProcedureValidationObject
): Array<ProcedureValidationError> {
  return filterProcedureValidationErrors(validationErrors, {
    primaryObjectId: primaryObject.id,
    primaryObjectType: primaryObject.objectType,
  }).concat(
    filterProcedureValidationErrors(validationErrors, {
      parentObjectId: primaryObject.id,
      parentObjectType: primaryObject.objectType,
    })
  );
}

export function findParameterTableValidationErrors(
  validationErrors: Array<ProcedureValidationError>,
  methodCid: string
): Array<ProcedureValidationError> {
  const selectedMethodValidationErrors = findRelevantProcedureValidationErrors(validationErrors, {
    id: methodCid,
    objectType: ProcedureValidationObjectType.METHOD,
  });

  return [
    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
      primaryObjectType: ProcedureValidationObjectType.DATA_PARAMETER,
    }),
    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
      primaryObjectType: ProcedureValidationObjectType.MATERIAL_INPUT,
    }),
    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
      primaryObjectType: ProcedureValidationObjectType.MATERIAL_OUTPUT,
    }),
    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
      primaryObjectType: ProcedureValidationObjectType.EQUIPMENT,
    }),
    // Product limit errors: include procedure-wide ones and per-method ones for this method
    ...filterProcedureValidationErrors(validationErrors, {
      errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
    }).filter((e) => e.parentObjects.length === 0 || e.parentObjects.some((o) => o.id === methodCid)),
  ];
}

export function findTestsTabValidationErrors(
  validationErrors: Array<ProcedureValidationError>,
  methodCid: string
): Array<ProcedureValidationError> {
  const selectedMethodValidationErrors = findRelevantProcedureValidationErrors(validationErrors, {
    id: methodCid,
    objectType: ProcedureValidationObjectType.METHOD,
  });

  return [
    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
      primaryObjectType: ProcedureValidationObjectType.TEST_DEFINITION,
    }),
    ...filterProcedureValidationErrors(selectedMethodValidationErrors, {
      primaryObjectType: ProcedureValidationObjectType.DILUTION_PLAN,
    }),
  ];
}

export enum ProcedureValidationErrorSeparator {
  PERIOD = '.',
  NEWLINE = '\n',
  PERIOD_SPACE = '. ',
  PERIOD_SPACE_NEWLINE = '. \n',
}

export const DEFAULT_ERROR_MESSAGE_SEPARATOR = ProcedureValidationErrorSeparator.PERIOD_SPACE_NEWLINE;

/**
 * Extracts unique error messages from pre-filtered validation errors to build a string tooltip.
 */
export function buildTooltipFromErrors(
  validationErrors: Array<ProcedureValidationError>,
  separator: ProcedureValidationErrorSeparator = DEFAULT_ERROR_MESSAGE_SEPARATOR
): string | null {
  if (validationErrors.length === 0) {
    return null;
  }

  const message = _.unique(validationErrors.map((error) => error.errorMessage)).join(separator);

  return separator === ProcedureValidationErrorSeparator.NEWLINE ? message : message.concat('.');
}

/**
 * Compiles a list of procedure errors into an object that splits errors into errors addressable from
 * flowchart view and method view. Flowchart view has errors for a method and an edge. The rest are errors
 * specific within a method. For each section, the errors are counted based on their occurrence and a map of
 * error message with count returned for each error.
 *
 * @param validationErrors
 * @returns Object map of flowchart errors with count and map of method error counts by method cid
 */
export function buildProcedureValidationErrorsForPanel(
  validationErrors: Array<ProcedureValidationError>
): ProcedureValidationErrorsForPanel {
  const flowchartErrorCounts: {[errorMessage: string]: number} = {};
  const methodErrorCountsByMethodCid: {[methodId: string]: {[errorMessage: string]: number}} = {};

  for (const validationError of validationErrors) {
    const objectType = validationError.primaryObject.objectType;
    if (
      [
        ProcedureValidationObjectType.MATERIAL_EDGE,
        ProcedureValidationObjectType.METHOD,
        ProcedureValidationObjectType.PROCEDURE,
      ].includes(objectType)
    ) {
      if (flowchartErrorCounts.hasOwnProperty(validationError.errorMessage)) {
        flowchartErrorCounts[validationError.errorMessage] += 1;
      } else {
        flowchartErrorCounts[validationError.errorMessage] = 1;
      }
    } else if (
      [
        ProcedureValidationObjectType.MATERIAL_INPUT,
        ProcedureValidationObjectType.MATERIAL_OUTPUT,
        ProcedureValidationObjectType.DATA_PARAMETER,
        ProcedureValidationObjectType.EQUIPMENT,
        ProcedureValidationObjectType.STEP,
        ProcedureValidationObjectType.TEST_DEFINITION,
        ProcedureValidationObjectType.DILUTION_PLAN,
      ].includes(objectType)
    ) {
      assert(validationError.parentObjects.length === 1, 'Unexpected number of parents for error');
      assert(
        validationError.parentObjects[0].objectType === ProcedureValidationObjectType.METHOD,
        'Unexpected parent object type for error',
        {parentObject: validationError.parentObjects[0]}
      );
      const methodId = validationError.parentObjects[0].id;
      if (methodErrorCountsByMethodCid.hasOwnProperty(methodId)) {
        if (methodErrorCountsByMethodCid[methodId].hasOwnProperty(validationError.errorMessage)) {
          methodErrorCountsByMethodCid[methodId][validationError.errorMessage] += 1;
        } else {
          methodErrorCountsByMethodCid[methodId][validationError.errorMessage] = 1;
        }
      } else {
        methodErrorCountsByMethodCid[methodId] = {[validationError.errorMessage]: 1};
      }
    }
  }
  return {
    flowchartErrorCounts,
    methodErrorCountsByMethodCid,
  };
}

/**
 * Similar to buildProcedureValidationErrorsForPanel but starts with
 * ProcessFlowchartValidationErrors and attributes them to method vs flowchart
 * view based on if they can be attributed to a node in the flowchart.
 */
export function buildValidationErrorsForPanelFromProcessFlowchartValidationErrors(
  validationErrors: Array<ProcessFlowchartValidationError>,
  nodeIdToNodeNameMap: Record<string, string>
): ProcedureValidationErrorsForPanel {
  const flowchartErrorCounts: {[errorMessage: string]: number} = {};
  const methodErrorCountsByMethodCid: {[nodeId: string]: {[errorMessage: string]: number}} = {};

  for (const validationError of validationErrors) {
    const {object, parentObjects, errorMessage} = validationError;

    let nodeId: string | undefined;

    if (object?.clientId && nodeIdToNodeNameMap.hasOwnProperty(object.clientId)) {
      nodeId = object.clientId;
    } else {
      // If a parent object is present, check if any of the parents are nodes in
      // the flowchart to attribute the error to a node
      nodeId = parentObjects?.find(({clientId}) => nodeIdToNodeNameMap[clientId] != null)?.clientId;
    }

    if (nodeId != null) {
      methodErrorCountsByMethodCid[nodeId] ??= {};
      methodErrorCountsByMethodCid[nodeId][errorMessage] =
        (methodErrorCountsByMethodCid[nodeId][errorMessage] ?? 0) + 1;
    } else {
      flowchartErrorCounts[errorMessage] = (flowchartErrorCounts[errorMessage] ?? 0) + 1;
    }
  }

  return {
    flowchartErrorCounts,
    methodErrorCountsByMethodCid,
  };
}

/**
 * Extract the current errors that were not in the previous errors list.
 * Note, this may eventually be an inefficient comparison, will defer until there are performance impacts
 */
export function findNewValidationErrors(
  previousValidationErrors: Array<ProcedureValidationError>,
  currentValidationErrors: Array<ProcedureValidationError>
): Array<ProcedureValidationError> {
  return currentValidationErrors.filter(
    (error) => previousValidationErrors.find((e) => isEqual(error, e)) == null
  );
}

/**
 * Check if there are entities in a typed value that have tag schemas different from specified tag schema
 * @param entityTypedValue
 * @param tagSchemaId
 * @param bioentitiesForProcedures
 */
function getMismatchingTagSchemaEntityNames(
  entityTypedValue: BioentityLinkSchemaFieldTypedValue,
  tagSchemaId: string,
  bioentitiesForProcedures: Array<BioentityForProcedure>
): Array<string> {
  return entityTypedValue.targetFiles
    .filter((targetFile) => {
      const entityInfo = bioentitiesForProcedures.find((entityInfo) => entityInfo.id === targetFile.id);
      // Only flag as mismatched if we found the entity AND its schema doesn't match - possible that entity is not loaded yet
      return entityInfo != null && entityInfo.tagSchema?.id !== tagSchemaId;
    })
    .map((targetFile) => targetFile.name);
}

function getMismatchingSchemaInterfaceEntityNames(
  entityTypedValue: BioentityLinkSchemaFieldTypedValue,
  schemaInterfaceId: string,
  bioentitiesForProcedures: Array<BioentityForProcedure>,
  tagSchemasForProcedures: Array<TagSchemaForProcedures>
): Array<string> {
  return entityTypedValue.targetFiles
    .filter((targetFile) => {
      const entityInfo = bioentitiesForProcedures.find((e) => e.id === targetFile.id);
      if (entityInfo == null) {
        return false;
      }
      if (entityInfo.tagSchema == null) {
        return true;
      }
      const entityTagSchema = tagSchemasForProcedures.find((ts) => ts.id === entityInfo.tagSchema!.id);
      if (entityTagSchema == null) {
        return true;
      }
      return !entityTagSchema.interfaces.some((iface) => iface.id === schemaInterfaceId);
    })
    .map((targetFile) => targetFile.name);
}

/**
 * Checks if an entity's tag schema matches the parameter dataType. An error string is compiled based on the
 * error type and returned.
 */
export function getEntitySchemaMismatchWithParameterSettingErrorMessage(
  entityTypedValue: BioentityLinkSchemaFieldTypedValue,
  parameterSetting: ProcedureEquipmentSetting | ProcedureMaterialInputSetting,
  errorType: ProcedureValidationErrorType,
  context: ProcedureValidationContext
): string | null {
  if (entityTypedValue.targetFiles.length === 0) {
    return null;
  }
  const {bioentitiesForProcedures, tagSchemas, tagSchemasForProcedures} = context;
  const dataType = isEquipmentSettingComponent(parameterSetting)
    ? parameterSetting.equipmentVersion.dataType
    : parameterSetting.materialInputVersion.dataType;

  // Assuming that the tag schema is a valid selection here. There is another validation error that
  // checks for valid schema selection.
  const tagSchema = isRequiredLinkTagSchema(dataType.requiredLink) ? dataType.requiredLink.tagSchema : null;

  if (tagSchema && bioentitiesForProcedures != null) {
    if (errorType === ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE) {
      // check entity tag schema with tag schema of the parameter setting
      const entityNamesWithError = getMismatchingTagSchemaEntityNames(
        entityTypedValue,
        tagSchema.id,
        bioentitiesForProcedures
      );
      if (entityNamesWithError.length > 0) {
        return `${getMethodComponentUnitString(parameterSetting)} is not of schema ${tagSchema.name}`;
      }
    } else if (errorType === ProcedureValidationErrorType.MISMATCH_MODELS_TYPE && tagSchemas) {
      // check entity tag schema with model field of tag schema of the parameter setting
      const tagSchemaIds = tagSchemas.map((ts) => ts.id);
      const targetTagSchemaIndex = tagSchemaIds.indexOf(tagSchema.id);
      if (targetTagSchemaIndex === -1) {
        return `You do not have permission to view some equipment schema, or that schema does not exist`;
      }
      const tagSchemaWithFields = tagSchemas[targetTagSchemaIndex];
      const modelRequiredLink = getModelRequiredLinkFromTagSchema(tagSchemaWithFields);
      if (!isRequiredLinkTagSchema(modelRequiredLink)) {
        return 'Equipment schema does not have appropriate Model field';
      }

      const entityNamesWithError = getMismatchingTagSchemaEntityNames(
        entityTypedValue,
        modelRequiredLink.tagSchema.id,
        bioentitiesForProcedures
      );
      if (entityNamesWithError.length > 0) {
        return `Model is not compatible with the selected schema's restrictions. ${entityNamesWithError.join(
          ','
        )} ${entityNamesWithError.length > 1 ? 'are' : 'is'} not of schema type ${tagSchema.name}`;
      }
    }
  }

  if (
    isRequiredLinkCustomSchemaInterface(dataType.requiredLink) &&
    bioentitiesForProcedures != null &&
    tagSchemasForProcedures != null &&
    errorType === ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE
  ) {
    const schemaInterface = dataType.requiredLink.schemaInterface;
    const entityNamesWithError = getMismatchingSchemaInterfaceEntityNames(
      entityTypedValue,
      schemaInterface.id,
      bioentitiesForProcedures,
      tagSchemasForProcedures
    );
    if (entityNamesWithError.length > 0) {
      return `${getMethodComponentUnitString(parameterSetting)} is not of fieldset ${schemaInterface.name}`;
    }
  }

  return null;
}

export interface ProcedureValidationContext {
  tagSchemas: Array<{
    id: string;
    name: string;
    isContainable: boolean;
    allFields: Array<SchemaField>;
    archiveRecord: ArchiveRecord | null;
  }> | null; // null = not loaded yet
  tagSchemasForProcedures: Array<TagSchemaForProcedures> | null; // null = not loaded yet
  bioentitiesForProcedures: Array<BioentityForProcedure> | null; // null = not loaded yet
  archivedLibraryParameterDefinitionIds: Set<string> | null; // null = not loaded yet
  schemaInterfaces: Array<SchemaInterfaceWithFields> | null; // null = not loaded yet
  procedureType: ProcedureType;
}

/**
 * Type guard to check if an unknown value is a ProcedureValidationContext
 */
export function isProcedureValidationContext(context: unknown): context is ProcedureValidationContext {
  if (!context || typeof context !== 'object') {
    return false;
  }
  const obj = context as ProcedureValidationContext;
  return (
    obj.tagSchemas != null &&
    obj.tagSchemasForProcedures != null &&
    obj.bioentitiesForProcedures != null &&
    obj.archivedLibraryParameterDefinitionIds != null &&
    Array.isArray(obj.tagSchemas) &&
    Array.isArray(obj.tagSchemasForProcedures) &&
    Array.isArray(obj.bioentitiesForProcedures) &&
    obj.archivedLibraryParameterDefinitionIds instanceof Set &&
    obj.procedureType != null
  );
}

export function extractMethodComponentsNames(components: Array<MethodComponent>): Array<string> {
  return components.map((c) => getMethodComponentName(c));
}

export function extractDuplicateNames(allNames: Array<string>): Array<string> {
  const counter = _.countBy(allNames, (name) => name);
  return Object.keys(counter).filter((name) => counter[name] > 1);
}

export function createErrorsForMethodComponentsWithMatchingName(
  methodComponents: Array<MethodComponent>,
  objectType: ProcedureValidationObjectType,
  nameToCompare: string,
  errorType: ProcedureValidationErrorType,
  errorMessage: string,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  return methodComponents
    .filter((p) => getMethodComponentName(p) === nameToCompare)
    .map((p) => ({
      errorMessage,
      type: errorType,
      primaryObject: {id: getMethodComponentCid(p), objectType},
      parentObjects,
    }));
}

export function extractMethodNodesFromProcessFlowchartForValidation(
  processFlowchartForValidation: ProcessFlowchartForValidation
): Array<MethodNode> {
  return processFlowchartForValidation.nodes
    .map((node) => extractMethodNodeFromProcessNode(node))
    .filter((node) => node != null);
}
